/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Sagas for Wizard Report component
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.4.0
 * @since 1.4.0
 */

import get from 'lodash/get';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { wizardReportSetParamsAction, wizardStorageSaveStepAction } from '../../wizardActions';
import { WIZARD_REPORT_STORAGE_SAVE } from '../../wizardConstants';
import { initialWizardNotificationParam } from '../../wizardReducer';
import { wizardStorageLoadStepsSaga } from '../../wizardSagas';
import { makeSelectWizardBasketSnapshotId } from '../../wizardSelectors';
import { TWizardMetaSuccessAction } from '../../wizardTypes';
import { EWizardReportParam } from './WizardReportConstants';
import { makeSelectWizardReportParam } from './wizardReportSelectors';
import { IWizardReportParam } from './wizardReportTypes';

export function* wizardReportValidateSaga(): SagaIterator  {
    let isStepValid = true;
    const email = yield select(makeSelectWizardReportParam(EWizardReportParam.EMAIL));
    const phone = yield select(makeSelectWizardReportParam(EWizardReportParam.PHONE));
    const reportDestEmail = yield select(makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST));
    const reportCompressionPassword = yield select(makeSelectWizardReportParam(EWizardReportParam.ZIP_PASSWORD));

    if (email.isEnabled && (!email.value || !email.isValid)) isStepValid = false;
    if (phone.isEnabled && (!phone.value || !phone.isValid)) isStepValid = false;
    if ((reportDestEmail.isEnabled) && (!reportDestEmail.value || !reportDestEmail.isValid)) isStepValid = false;
    if ((reportCompressionPassword.isEnabled)
        && (!reportCompressionPassword.value || !reportCompressionPassword.isValid)) isStepValid = false;

    return isStepValid;
}

export function* wizardReportStorageRestoreSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const PHONE_LENGTH = 10;
    const metaEmail = action.payload?.step.userContacts?.email ?? '';
    const metaPhone = action.payload?.step.userContacts?.phone ?? '';

    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = get(localStorageState, `${basketSnapshotId}`, {});
    const email: IWizardReportParam = localStorageParams.notificationEmail ??
        { ...initialWizardNotificationParam, value: metaEmail };
    const phone: IWizardReportParam = localStorageParams.notificationPhone ??
        { ...initialWizardNotificationParam, value: metaPhone.slice(-PHONE_LENGTH) };
    const reportDestEmail: IWizardReportParam = localStorageParams.reportDestEmail ??
        { ...initialWizardNotificationParam, value: metaEmail };
    const reportNeedCompression: IWizardReportParam = localStorageParams.reportNeedCompression ??
        { ...initialWizardNotificationParam };
    const reportCompressionPassword: IWizardReportParam = localStorageParams.reportCompressionPassword ??
        { ...initialWizardNotificationParam, value: '' };

    yield put(wizardReportSetParamsAction({
        [EWizardReportParam.EMAIL]: email,
        [EWizardReportParam.PHONE]: phone,
        [EWizardReportParam.EMAIL_DEST]: reportDestEmail,
        [EWizardReportParam.ZIP]: reportNeedCompression,
        [EWizardReportParam.ZIP_PASSWORD]: reportCompressionPassword,
    }));
}

export function* wizardReportStorageSaveSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = localStorageState[basketSnapshotId];

    const notificationEmail: IWizardReportParam =
        yield select(makeSelectWizardReportParam(EWizardReportParam.EMAIL));
    const notificationPhone: IWizardReportParam =
        yield select(makeSelectWizardReportParam(EWizardReportParam.PHONE));
    const reportDestEmail: IWizardReportParam =
        yield select(makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST));
    const reportNeedCompression: IWizardReportParam =
        yield select(makeSelectWizardReportParam(EWizardReportParam.ZIP));
    const reportCompressionPassword: IWizardReportParam =
        yield select(makeSelectWizardReportParam(EWizardReportParam.ZIP_PASSWORD));

    const params = {
        ...localStorageParams,
        notificationEmail,
        notificationPhone,
        reportDestEmail,
        reportNeedCompression,
        reportCompressionPassword,
    };

    yield put(wizardStorageSaveStepAction({ params }))
}

export function* wizardReportSagas(): SagaIterator {
    yield takeEvery(WIZARD_REPORT_STORAGE_SAVE, wizardReportStorageSaveSaga);
}
